import { useState, useEffect } from 'react';
import type { Asset, AssetStatus, AssetCategory, AssetYearValue, EquipmentLink } from '@/types/asset';
import type { AssetLookupText } from '@/types/financials';
// Category, Location, SubLedger, FinancialsSupplierAddress, Supplier, SystemYear removed - unused
import { dataService } from '@/services/data-service';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useSystemOptions } from '@/contexts/SystemOptionsContext';
import { useLanguageSettings } from '@/contexts/LanguageSettingsContext';
import { useAssetEditorData, useYearValues, useSupplierAddresses } from '@/hooks/use-asset-editor-data';
// financialsService import removed - now handled by useAssetEditorData hook
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Save, X, AlertCircle, Plus, Trash2, Loader2, FileText, Settings, Package, Wrench, StickyNote, FileCheck, Image, Check, ChevronsUpDown, Edit } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DocumentsTab } from '@/components/documents/documents-tab';
// ComplianceTab import removed - not currently used in this component
import { AssetAlertsTab } from './asset-alerts-tab';
// AssetAuditTab import removed - component not yet implemented
import { BarcodeQRCodeSection } from './barcode-qrcode-section';
import { AssetPhotosTab } from './asset-photos-tab';
import { formatDateForInput } from '@/utils/date-utils';
import type { AssetPhoto } from './asset-photos-tab';
import { AssetConfirmChangesModal } from './asset-confirm-changes-modal';
import { AssetUnsavedChangesModal } from './asset-unsaved-changes-modal';
import { cn } from '@/lib/utils';
import { validateImageFile } from '@/lib/image-validation';
import { getAssetPrimaryPhotoUrl } from '@/lib/asset-utils';

interface AssetEditorProps {
  readonly asset: Asset;
  readonly open?: boolean; // Keep for backward compatibility
  readonly onClose: () => void;
  readonly onSave: (asset: Asset) => void;
  readonly mode?: 'create' | 'edit' | 'view' | 'page';
}

// Default empty year values for when data is loading or not available
const emptyYearValues: AssetYearValue = {
  year: new Date().getFullYear().toString(),
  cost: {
    startOfYear: 0,
    additions: 0,
    disposals: 0,
    revaluation: 0,
    endOfYear: 0,
  },
  depreciation: {
    startOfYear: 0,
    charge: 0,
    disposals: 0,
    revaluation: 0,
    endOfYear: 0,
  },
  netBookValue: {
    startOfYear: 0,
    endOfYear: 0,
  },
};

type TabValue = 'details' | 'values' | 'settings' | 'supplier' | 'equipment' | 'custom-fields' | 'notes' | 'documents' | 'alerts' | 'photos';

interface TabConfig {
  value: TabValue;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  hideInCreate?: boolean;
}

const tabs: TabConfig[] = [
  { value: 'details', label: 'Details', icon: FileText },
  { value: 'values', label: 'Values', icon: Package, hideInCreate: true },
  { value: 'settings', label: 'Settings', icon: Settings },
  { value: 'supplier', label: 'Supplier', icon: Package },
  { value: 'equipment', label: 'Equipment', icon: Wrench },
  { value: 'custom-fields', label: 'Custom Fields', icon: FileText },
  { value: 'notes', label: 'Notes', icon: StickyNote },
  { value: 'documents', label: 'Documents', icon: FileCheck, hideInCreate: true },
  { value: 'alerts', label: 'Alerts', icon: AlertCircle, hideInCreate: true },
  { value: 'photos', label: 'Photos', icon: Image },
];

// User Key Combobox Component
interface UserKeyComboboxProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly options: AssetLookupText[];
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly loading?: boolean;
}

function UserKeyCombobox({ 
  id, 
  label, 
  value, 
  options, 
  onChange, 
  disabled, 
  loading 
}: UserKeyComboboxProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? '');

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={disabled || loading}
          >
            {inputValue ?? "Select or type..."}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput 
              placeholder="Search or type..." 
              value={inputValue}
              onValueChange={(value) => {
                setInputValue(value);
                onChange(value);
              }}
            />
            <CommandEmpty>
              {inputValue ? `Use "${inputValue}"` : "No options found"}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.text}
                  onSelect={(currentValue) => {
                    setInputValue(currentValue);
                    onChange(currentValue);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      inputValue === option.text ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.text}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function AssetEditor({ asset, open, onClose, onSave, mode }: AssetEditorProps): React.ReactElement {
  const { options: systemOptions } = useSystemOptions();
  const { getLabel } = useLanguageSettings();
  const [editedAsset, setEditedAsset] = useState<Asset>(asset);
  const [originalAsset, setOriginalAsset] = useState<Asset>(asset);
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [, setImagePreview] = useState<string | null>(getAssetPrimaryPhotoUrl(asset) ?? null);
  const [linkedEquipment, setLinkedEquipment] = useState<EquipmentLink[]>(asset.linkedEquipment ?? []);
  const [activeTab, setActiveTab] = useState<TabValue>('details');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Determine mode based on prop or asset id
  const isCreateMode = mode === 'create' || asset.id?.startsWith('new-') || false;
  const isViewMode = (mode === 'view') || (mode === 'edit' && !isEditing);
  const isEditMode = mode === 'edit' || (!isCreateMode && !isViewMode);

  // Use custom hooks for data fetching
  const editorData = useAssetEditorData();
  const yearValuesState = useYearValues(asset.id, activeTab, isCreateMode);
  const supplierAddressesState = useSupplierAddresses(editedAsset.supplierId);

  // Extract data from hooks for easier access
  const categories = editorData.categories.data;
  const categoriesLoading = editorData.categories.loading;
  const categoriesError = editorData.categories.error;
  const locations = editorData.locations.data;
  const locationsLoading = editorData.locations.loading;
  const locationsError = editorData.locations.error;
  const subLedgers = editorData.subLedgers.data;
  const subLedgersLoading = editorData.subLedgers.loading;
  const subLedgersError = editorData.subLedgers.error;
  const systemYears = editorData.systemYears.data;
  const systemYearsLoading = editorData.systemYears.loading;
  const systemYearsError = editorData.systemYears.error;
  const suppliers = editorData.suppliers.data;
  const suppliersLoading = editorData.suppliers.loading;
  const suppliersError = editorData.suppliers.error;
  const userKey1Options = editorData.userKeys.data.key1;
  const userKey2Options = editorData.userKeys.data.key2;
  const userKey3Options = editorData.userKeys.data.key3;
  const userKey4Options = editorData.userKeys.data.key4;
  const userKey5Options = editorData.userKeys.data.key5;
  const userKeysLoading = editorData.userKeys.loading;
  const yearValues = yearValuesState.data;
  const yearValuesLoading = yearValuesState.loading;
  const yearValuesError = yearValuesState.error;
  const financialsSupplierAddresses = supplierAddressesState.data;
  const supplierAddressesLoading = supplierAddressesState.loading;

  // Reset editing state when asset changes or modal opens
  useEffect(() => {
    setIsEditing(false);
  }, [asset.id, open]);

  // Store original asset when asset prop changes
  useEffect(() => {
    setOriginalAsset(asset);
    setEditedAsset(asset);
  }, [asset]);

  // Initialize defaults for Create mode
  // This applies the default depreciation settings from Asset Options when creating a new asset
  useEffect(() => {
    if (isCreateMode) {
      const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
      
      // Fetch and apply default depreciation settings from Asset Options
      const applyDefaults = async () => {
        try {
          const assetOptions = await dataService.getAssetOptions();
          const defaultSettings = assetOptions?.defaultSettings;
          
          // Get transaction defaults (nominal accounts and costing project)
          const transactionDefaults = assetOptions?.transactions;
          const nominalAccounts = transactionDefaults?.nominalAccounts;
          const costingDefaults = transactionDefaults?.costing;
          
          // Only apply depreciation defaults if they exist and depreciation is not disabled globally
          if (defaultSettings && !defaultSettings.doNotDepreciate) {
            setEditedAsset((prev) => ({
              ...prev,
              code: '',
              acquisitionCost: 0.00,
              status: 'active' as const,
              acquisitionDate: today,
              // Apply the 5 default depreciation settings from Asset Options
              // (excluding forceTransfersOnChangingCategory which is not an asset field)
              depreciationMethod: defaultSettings.defaultMethod,
              depreciationPercentage: defaultSettings.defaultPercentage,
              depreciationYears: defaultSettings.defaultPeriodYears,
              postingFrequency: defaultSettings.defaultFrequency,
              doNotDepreciate: defaultSettings.doNotDepreciate,
              // Apply default nominal accounts from Asset Options Transactions tab
              assetCostNominal: nominalAccounts?.assetCost ?? prev.assetCostNominal,
              depreciationBSNominal: nominalAccounts?.depreciationBS ?? prev.depreciationBSNominal,
              depreciationPLNominal: nominalAccounts?.depreciationPL ?? prev.depreciationPLNominal,
              disposalsNominal: nominalAccounts?.disposal ?? prev.disposalsNominal,
              revaluationBSNominal: nominalAccounts?.revaluationBS ?? prev.revaluationBSNominal,
              revaluationPLNominal: nominalAccounts?.revaluationPL ?? prev.revaluationPLNominal,
              disposalsBankAccountNominal: nominalAccounts?.disposalsBankAccount ?? prev.disposalsBankAccountNominal,
              additionsWIPBankAccountNominal: nominalAccounts?.additionsWIPBankAcc ?? prev.additionsWIPBankAccountNominal,
              // Apply default costing project from Asset Options Transactions tab (using 'addition' as the default for new assets)
              project: costingDefaults?.addition ?? prev.project,
            }));
            
            console.log('✓ Applied default depreciation settings from Asset Options:', {
              method: defaultSettings.defaultMethod,
              percentage: defaultSettings.defaultPercentage,
              years: defaultSettings.defaultPeriodYears,
              frequency: defaultSettings.defaultFrequency,
              doNotDepreciate: defaultSettings.doNotDepreciate,
            });
            
            if (nominalAccounts) {
              console.log('✓ Applied default nominal accounts from Asset Options:', nominalAccounts);
            }
            
            if (costingDefaults?.addition) {
              console.log('✓ Applied default costing project from Asset Options:', costingDefaults.addition);
            }
          } else {
            // Fallback if no default settings found or depreciation is disabled globally
            setEditedAsset((prev) => ({
              ...prev,
              code: '',
              acquisitionCost: 0.00,
              status: 'active' as const,
              acquisitionDate: today,
              doNotDepreciate: defaultSettings?.doNotDepreciate ?? false,
              // Still apply nominal accounts and costing project even if depreciation is disabled
              assetCostNominal: nominalAccounts?.assetCost ?? prev.assetCostNominal,
              depreciationBSNominal: nominalAccounts?.depreciationBS ?? prev.depreciationBSNominal,
              depreciationPLNominal: nominalAccounts?.depreciationPL ?? prev.depreciationPLNominal,
              disposalsNominal: nominalAccounts?.disposal ?? prev.disposalsNominal,
              revaluationBSNominal: nominalAccounts?.revaluationBS ?? prev.revaluationBSNominal,
              revaluationPLNominal: nominalAccounts?.revaluationPL ?? prev.revaluationPLNominal,
              disposalsBankAccountNominal: nominalAccounts?.disposalsBankAccount ?? prev.disposalsBankAccountNominal,
              additionsWIPBankAccountNominal: nominalAccounts?.additionsWIPBankAcc ?? prev.additionsWIPBankAccountNominal,
              project: costingDefaults?.addition ?? prev.project,
            }));
            
            if (defaultSettings?.doNotDepreciate) {
              console.log('ℹ Default depreciation is disabled in Asset Options');
            }
            
            if (nominalAccounts) {
              console.log('✓ Applied default nominal accounts from Asset Options:', nominalAccounts);
            }
            
            if (costingDefaults?.addition) {
              console.log('✓ Applied default costing project from Asset Options:', costingDefaults.addition);
            }
          }
          
          // Reset hasChanges to false after applying initial defaults
          // This ensures the user can close the dialog without a confirmation prompt
          // if they haven't made any manual changes
          setHasChanges(false);
        } catch (error) {
          console.error('Failed to fetch asset options:', error);
          // Fallback to basic defaults on error - user can still manually set values
          setEditedAsset((prev) => ({
            ...prev,
            code: '',
            acquisitionCost: 0.00,
            status: 'active' as const,
            acquisitionDate: today,
          }));
          
          // Reset hasChanges even on error
          setHasChanges(false);
        }
      };
      
      applyDefaults();
    }
  }, [isCreateMode]);

  // Asset code will be auto-generated by backend on save when auto-numbering is enabled
  // No need to generate it on the frontend

  const getFrequencyValue = (frequency: string | undefined): number => {
    const frequencyMap: Record<string, number> = {
      'Monthly': 12,
      'Quarterly': 4,
      'Half Yearly': 2,
      'Yearly': 1,
    };
    return frequencyMap[frequency ?? ''] ?? 12;
  };

  const handleMethodChange = (updated: Asset, method: string): void => {
    if (method === 'Sum of Digits') {
      updated.postingFrequency = 'Monthly';
      if (updated.depreciationYears) {
        updated.depreciationYears = Math.round(updated.depreciationYears);
        updated.totalPostings = updated.depreciationYears * 12;
      }
    } else if (method === 'Reducing Balance' || method === 'None') {
      updated.depreciationYears = 0;
      updated.totalPostings = 0;
      updated.baseValueOn = 'original';
    }
  };

  const handleYearsChange = (updated: Asset, value: number): void => {
    const years = value ?? 0;
    const freq = getFrequencyValue(updated.postingFrequency);
    updated.totalPostings = years * freq;
    if (years > 0) updated.depreciationPercentage = 100 / years;
    if (updated.depreciationMethod === 'Sum of Digits' && years > 0) {
      updated.depreciationYears = Math.round(years);
      updated.totalPostings = Math.round(years) * freq;
    }
  };

  const handlePercentageChange = (updated: Asset, value: number): void => {
    const percentage = value ?? 0;
    if (percentage > 0) {
      const freq = getFrequencyValue(updated.postingFrequency);
      const years = 100 / percentage;
      updated.depreciationYears = years;
      updated.totalPostings = years * freq;
      updated.depreciationPercentage = 100 / years;
    }
  };

  const applyDepreciationAutoCalc = (updated: Asset, field: keyof Asset, value: any): void => {
    if (field === 'depreciationMethod') handleMethodChange(updated, value);
    else if (field === 'postingFrequency') updated.totalPostings = (updated.depreciationYears ?? 0) * getFrequencyValue(value);
    else if (field === 'depreciationYears') handleYearsChange(updated, value);
    else if (field === 'depreciationPercentage') handlePercentageChange(updated, value);
  };

  const handleChange = (field: keyof Asset, value: any) => {
    setEditedAsset((prev) => {
      const updated = { ...prev, [field]: value };
      applyDepreciationAutoCalc(updated, field, value);
      return updated;
    });
    setHasChanges(true);
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleCategoryChange = async (categoryValue: AssetCategory) => {
    // Update the category first and clear sub-category when category changes
    handleChange('category', categoryValue);
    handleChange('subCategory', '');

    // Only populate category-specific defaults in create mode
    // This will override the global defaults from Asset Options
    if (!isCreateMode) {
      return;
    }

    // Find the selected category object
    const selectedCategory = categories.find(
      (cat) => cat.description === categoryValue || cat.code === categoryValue
    );

    if (!selectedCategory) {
      console.warn('Selected category not found in categories list');
      return;
    }

    try {
      // Fetch category-specific defaults
      const defaults = await dataService.getCategoryDefaults(selectedCategory.id);

      // Populate depreciation settings with category-specific values
      // These will override the global defaults from Asset Options
      setEditedAsset((prev) => ({
        ...prev,
        category: categoryValue,
        subCategory: '',
        depreciationMethod: defaults.method,
        depreciationPercentage: defaults.percentage,
        depreciationYears: defaults.years,
        postingFrequency: defaults.frequency,
      }));

      console.log('✓ Applied category-specific depreciation defaults:', {
        category: categoryValue,
        method: defaults.method,
        percentage: defaults.percentage,
        years: defaults.years,
        frequency: defaults.frequency,
      });
    } catch (error) {
      console.error('Failed to fetch category defaults:', error);
      // Don't show error to user - just log it
      // User can still manually set values or use global defaults
    }
  };

  // Get sub-categories for the selected category
  const selectedCategoryForSubCategories = categories.find(
    (cat) => cat.description === editedAsset.category || cat.code === editedAsset.category
  );
  const availableSubCategories = selectedCategoryForSubCategories?.subCategories ?? [];

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Validate required fields based on backend DTOs
    // Code is required (unless auto-numbering is enabled in create mode)
    if (!editedAsset.code && !(isCreateMode && systemOptions?.autoNumbering.enabled)) {
      newErrors.code = 'Asset code is required';
    }
    
    // Name (Description) is required
    if (!editedAsset.name || editedAsset.name.trim() === '') {
      newErrors.name = 'Description is required';
    }
    
    // Category is required
    if (!editedAsset.category || editedAsset.category.trim() === '') {
      newErrors.category = 'Category is required';
    }
    
    // Location is required
    if (!editedAsset.location || editedAsset.location.trim() === '') {
      newErrors.location = 'Location is required';
    }
    
    // Acquisition Cost is required and must be greater than 0
    if (editedAsset.acquisitionCost === undefined || 
        editedAsset.acquisitionCost === null || 
        editedAsset.acquisitionCost === 0) {
      newErrors.acquisitionCost = 'Acquisition value is required and must be greater than 0';
    } else if (editedAsset.acquisitionCost < 0) {
      newErrors.acquisitionCost = 'Acquisition cost cannot be negative';
    }
    
    // Acquisition Date is required
    if (!editedAsset.acquisitionDate || editedAsset.acquisitionDate.trim() === '') {
      newErrors.acquisitionDate = 'Acquisition date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!validate()) {
      // Scroll to top to show error messages
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // If in a dialog/sheet, scroll the content area
      const contentArea = document.querySelector('[role="dialog"] .overflow-auto, [role="dialog"] .overflow-y-auto');
      if (contentArea) {
        contentArea.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    
    console.log('handleSave called', { isCreateMode, isViewMode, isEditMode, mode });
    
    // Only show modal when editing existing assets (not in create mode)
    if (!isCreateMode) {
      console.log('Showing confirmation modal');
      setShowConfirmModal(true);
    } else {
      // For create mode, save directly
      console.log('Saving directly (create mode)');
      handleConfirmSave();
    }
  };

  const handleConfirmSave = () => {
    onSave({
      ...editedAsset,
      linkedEquipment,
      modifiedBy: 'Current User',
      modifiedDate: new Date().toISOString(),
    });
    setShowConfirmModal(false);
    setOriginalAsset(editedAsset); // Update original after save
    setHasChanges(false);
    setIsEditing(false); // Exit editing mode after save
  };

  const handleClose = (isOpen?: boolean | React.MouseEvent) => {
    // If called from Dialog's onOpenChange with true, ignore (trying to open)
    if (typeof isOpen === 'boolean' && isOpen) return;
    
    // In view mode, always allow closing without confirmation
    if (isViewMode) {
      setIsEditing(false); // Reset editing state
      onClose();
      return;
    }
    
    if (hasChanges) {
      setShowUnsavedChangesModal(true);
      // If user cancels, do nothing (dialog stays open)
    } else {
      setIsEditing(false); // Reset editing state
      onClose();
    }
  };

  const handleCancelClick = () => {
    // In view mode, always allow closing without confirmation
    if (isViewMode) {
      setIsEditing(false); // Reset editing state
      onClose();
      return;
    }
    
    // In edit mode while editing, cancel returns to read-only mode
    if (mode === 'edit' && isEditing) {
      if (hasChanges) {
        setShowUnsavedChangesModal(true);
      } else {
        setIsEditing(false);
        // Reset form to original asset data
        setEditedAsset(originalAsset);
        setHasChanges(false);
      }
    } else {
      // In create mode or other cases, close the modal
      if (hasChanges) {
        setShowUnsavedChangesModal(true);
      } else {
        setIsEditing(false); // Reset editing state
        onClose();
      }
    }
  };

  const handleConfirmLeave = () => {
    setShowUnsavedChangesModal(false);
    
    // In edit mode while editing, return to read-only mode
    if (mode === 'edit' && isEditing) {
      setIsEditing(false);
      // Reset form to original asset data
      setEditedAsset(originalAsset);
      setHasChanges(false);
    } else {
      // In other modes, close the modal
      setIsEditing(false); // Reset editing state
      onClose();
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: editedAsset.currency ?? 'GBP',
    }).format(value);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validationError = validateImageFile(file);
      if (validationError) {
        alert(validationError);
        e.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setImagePreview(dataUrl);
        // Create a new photo object and set it as primary
        const newPhoto: AssetPhoto = {
          id: `photo-${Date.now()}`,
          url: dataUrl,
          isPrimary: true,
          uploadedDate: new Date().toISOString(),
          fileName: file.name,
          fileSize: file.size,
        };
        // Update photos array - mark existing photos as not primary
        const existingPhotos = (editedAsset.photos ?? []).map(p => ({ ...p, isPrimary: false }));
        handleChange('photos', [...existingPhotos, newPhoto]);
      };
      reader.readAsDataURL(file);
    }
  };

  const visibleTabs= tabs.filter(tab => !tab.hideInCreate || !isCreateMode);

  // Content that will be rendered either in Sheet or directly
  const content = (
    <div className="flex h-full overflow-hidden">
          {/* Left Sidebar - Vertical Tabs - Hidden on small screens */}
          <div className={cn(
            "hidden md:flex w-64 border-r bg-white dark:bg-black flex-col shrink-0 h-full"
          )}>
            {/* Header */}
            <div className={cn(
              "p-6 border-b bg-white dark:bg-black",
              mode === 'edit' && !isViewMode && "bg-primary/5 dark:bg-primary/10"
            )}>
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#999]">Asset Records</span>
                  {mode === 'edit' && !isViewMode && (
                    <div className="bg-primary/90 text-primary-foreground px-2 py-0.5 text-xs font-medium rounded-full shadow-sm">
                      Editing
                    </div>
                  )}
                </div>
                <h2 className="text-lg font-semibold truncate" title={editedAsset.code ?? 'New Asset'}>
                  {editedAsset.code ?? 'New Asset'}
                </h2>
              </div>
            </div>

            {/* Vertical Tab Navigation */}
            <ScrollArea className="flex-1">
              <nav className="p-3 space-y-1">
                {visibleTabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.value}
                      onClick={() => setActiveTab(tab.value)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors",
                        activeTab === tab.value
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </ScrollArea>

            {/* Footer Actions */}
            <div className="p-4 border-t bg-white dark:bg-black space-y-2">
              <Button 
                variant="outline" 
                onClick={handleCancelClick}
                className="w-full"
              >
                <X className="h-4 w-4 mr-2" />
                {isViewMode ? 'Close' : 'Cancel'}
              </Button>
              {mode === 'edit' && isViewMode && (
                <Button 
                  onClick={handleEdit}
                  className="w-full"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              {!isViewMode && (
                <Button 
                  onClick={handleSave} 
                  disabled={!hasChanges}
                  className="w-full"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Mobile Header with Tab Dropdown */}
            <div className={cn(
              "md:hidden border-b bg-white dark:bg-black p-4",
              mode === 'edit' && !isViewMode && "bg-primary/5 dark:bg-primary/10"
            )}>
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-[#999]">Asset Records</span>
                    <h2 className="text-lg font-semibold truncate" title={editedAsset.code ?? 'New Asset'}>
                      {editedAsset.code ?? 'New Asset'}
                    </h2>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={handleClose}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobile-tab-select" className="text-sm">Section</Label>
                  <Select value={activeTab} onValueChange={(value: string) => setActiveTab(value as TabValue)}>
                    <SelectTrigger id="mobile-tab-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {visibleTabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                          <SelectItem key={tab.value} value={tab.value}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              <span>{tab.label}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                {mode === 'edit' && isViewMode ? (
                  <Button 
                    onClick={handleEdit}
                    className="w-full"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                ) : !isViewMode && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={handleClose}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSave} 
                      disabled={!hasChanges}
                      className="flex-1"
                    >
                      Save
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex-1 overflow-auto">
              <div className="w-full mx-auto p-4 md:p-8">
                {/* Details Tab */}
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {Object.keys(errors).length > 0 && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          <div className="font-semibold mb-1">Please correct the following errors:</div>
                          <ul className="list-disc list-inside space-y-1">
                            {Object.entries(errors).map(([field, message]) => (
                              <li key={field}>{message}</li>
                            ))}
                          </ul>
                        </AlertDescription>
                      </Alert>
                    )}
                    {!isViewMode && Object.keys(errors).length === 0 && (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          Fields marked with * are required
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Basic Fields */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Basic Information</CardTitle>
                      </CardHeader>
                      <CardContent className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="code">Code *</Label>
                          <Input
                            id="code"
                            value={editedAsset.code}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                              // Trim whitespace and validate alphanumeric + - and _
                              const value = e.target.value;
                              if (/^[a-zA-Z0-9_-]*$/.test(value)) {
                                handleChange('code', value);
                              }
                            }}
                            onBlur={(e) => {
                              // Trim whitespace on blur
                              const trimmed = e.target.value.trim();
                              if (trimmed !== e.target.value) {
                                handleChange('code', trimmed);
                              }
                            }}
                            disabled={
                              isViewMode || 
                              (isCreateMode && systemOptions?.autoNumbering.enabled) ||
                              (!isCreateMode && editedAsset.used)
                            }
                            readOnly={
                              isViewMode || 
                              (isCreateMode && systemOptions?.autoNumbering.enabled) ||
                              (!isCreateMode && editedAsset.used)
                            }
                            className={cn(
                              errors.code && 'border-red-500',
                              (isViewMode || 
                               (isCreateMode && systemOptions?.autoNumbering.enabled) ||
                               (!isCreateMode && editedAsset.used)) && 'bg-muted cursor-not-allowed'
                            )}
                            placeholder={
                              isCreateMode && systemOptions?.autoNumbering.enabled 
                                ? 'Auto-generated on save' 
                                : isCreateMode 
                                ? 'Enter asset code' 
                                : ''
                            }
                          />
                          {errors.code && (
                            <p className="text-sm text-red-500">{errors.code}</p>
                          )}
                          {!errors.code && isCreateMode && systemOptions?.autoNumbering.enabled && (
                            <p className="text-xs text-muted-foreground">Asset code will be automatically generated when you save</p>
                          )}
                          {!errors.code && !isCreateMode && editedAsset.used && (
                            <p className="text-xs text-muted-foreground">Asset code cannot be changed because it has transactions or other links</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="name">Description *</Label>
                          <Input
                            id="name"
                            value={editedAsset.name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('name', e.target.value)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                            className={`${errors.name ? 'border-red-500' : ''} ${isViewMode ? 'bg-muted cursor-not-allowed' : ''}`}
                          />
                          {errors.name && (
                            <p className="text-sm text-red-500">{errors.name}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="category">Category *</Label>
                          <Select
                            value={editedAsset.category}
                            onValueChange={(value: string) => handleCategoryChange(value as AssetCategory)}
                            disabled={isViewMode || categoriesLoading}
                          >
                            <SelectTrigger id="category" aria-label="Select category" className={errors.category ? 'border-red-500' : ''}>
                              <SelectValue placeholder={categoriesLoading ? "Loading categories..." : "Select a category"} />
                            </SelectTrigger>
                            <SelectContent>
                              {categoriesLoading ? (
                                <div className="flex items-center justify-center p-2">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  <span className="ml-2 text-sm">Loading...</span>
                                </div>
                              ) : categoriesError ? (
                                <div className="p-2 text-sm text-red-500">
                                  Failed to load categories
                                </div>
                              ) : categories.length === 0 ? (
                                <div className="p-2 text-sm text-muted-foreground">
                                  No categories available
                                </div>
                              ) : (
                                categories.map((cat) => (
                                  <SelectItem key={cat.id} value={cat.description || cat.code}>
                                    {cat.description || cat.code}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          {errors.category && (
                            <p className="text-sm text-red-500">{errors.category}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subCategory">Sub-Category</Label>
                          <Select
                            value={editedAsset.subCategory ?? ''}
                            onValueChange={(value: string) => handleChange('subCategory', value)}
                            disabled={isViewMode || !editedAsset.category || availableSubCategories.length === 0}
                          >
                            <SelectTrigger id="subCategory" aria-label="Select sub-category">
                              <SelectValue placeholder={
                                (() => {
                                  if (!editedAsset.category) return "Select a category first";
                                  if (availableSubCategories.length === 0) return "No sub-categories available";
                                  return "Select a sub-category (optional)";
                                })()
                              } />
                            </SelectTrigger>
                            <SelectContent>
                              {availableSubCategories.length === 0 ? (
                                <div className="p-2 text-sm text-muted-foreground">
                                  No sub-categories available for this category
                                </div>
                              ) : (
                                <>
                                  <SelectItem value="">None</SelectItem>
                                  {availableSubCategories.map((subCat) => (
                                    <SelectItem key={subCat.id} value={subCat.name}>
                                      {subCat.name}
                                    </SelectItem>
                                  ))}
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="status">Status</Label>
                          <Select
                            value={editedAsset.status}
                            onValueChange={(value: string) => handleChange('status', value as AssetStatus)}
                            disabled={isViewMode}
                          >
                            <SelectTrigger id="status" aria-label="Select status">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="inactive">Inactive</SelectItem>
                              <SelectItem value="maintenance">Maintenance</SelectItem>
                              <SelectItem value="disposed">Disposed</SelectItem>
                              <SelectItem value="pending">Pending</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="acquisitionDate">Date Acquired *</Label>
                          <Input
                            id="acquisitionDate"
                            type="date"
                            value={formatDateForInput(editedAsset.acquisitionDate)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('acquisitionDate', e.target.value)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                            className={cn(
                              errors.acquisitionDate && 'border-red-500',
                              isViewMode && 'bg-muted cursor-not-allowed'
                            )}
                          />
                          {errors.acquisitionDate && (
                            <p className="text-sm text-red-500">{errors.acquisitionDate}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="acquisitionCost">Acquisition Value *</Label>
                          <CurrencyInput
                            id="acquisitionCost"
                            value={editedAsset.acquisitionCost ?? 0}
                            onChange={(value) => {
                              handleChange('acquisitionCost', value);
                              if (isCreateMode) {
                                handleChange('netBookValue', value);
                              }
                            }}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                            className={cn(
                              errors.acquisitionCost && 'border-red-500',
                              isViewMode && 'bg-muted cursor-not-allowed'
                            )}
                          />
                          {errors.acquisitionCost && (
                            <p className="text-sm text-red-500">{errors.acquisitionCost}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="quantity">Quantity</Label>
                          <Input
                            id="quantity"
                            type="number"
                            value={editedAsset.quantity || 1}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('quantity', parseInt(e.target.value))}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                            className={isViewMode ? 'bg-muted cursor-not-allowed' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="serialNumber">Serial Number</Label>
                          <Input
                            id="serialNumber"
                            value={editedAsset.serialNumber ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('serialNumber', e.target.value)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                            className={isViewMode ? 'bg-muted cursor-not-allowed' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="location">Location *</Label>
                          <Select
                            value={editedAsset.location}
                            onValueChange={(value: string) => handleChange('location', value)}
                            disabled={isViewMode || locationsLoading}
                          >
                            <SelectTrigger id="location" aria-label="Select location" className={errors.location ? 'border-red-500' : ''}>
                              <SelectValue placeholder={locationsLoading ? "Loading locations..." : "Select a location"} />
                            </SelectTrigger>
                            <SelectContent>
                              {locationsLoading ? (
                                <div className="flex items-center justify-center p-2">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  <span className="ml-2 text-sm">Loading...</span>
                                </div>
                              ) : locationsError ? (
                                <div className="p-2 text-sm text-red-500">
                                  Failed to load locations
                                </div>
                              ) : locations.length === 0 ? (
                                <div className="p-2 text-sm text-muted-foreground">
                                  No locations available
                                </div>
                              ) : (
                                locations.map((loc) => (
                                  <SelectItem key={loc.id} value={loc.name}>
                                    {loc.name}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          {errors.location && (
                            <p className="text-sm text-red-500">{errors.location}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="resourceCode">Resource Code</Label>
                          <Input
                            id="resourceCode"
                            value={editedAsset.resourceCode ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('resourceCode', e.target.value)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                            className={isViewMode ? 'bg-muted cursor-not-allowed' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subLedger">Sub Ledger</Label>
                          <Select
                            value={editedAsset.subLedger ?? ''}
                            onValueChange={(value: string) => handleChange('subLedger', value)}
                            disabled={isViewMode || subLedgersLoading}
                          >
                            <SelectTrigger id="subLedger" aria-label="Select sub ledger">
                              <SelectValue placeholder={subLedgersLoading ? "Loading sub ledgers..." : "Select a sub ledger"} />
                            </SelectTrigger>
                            <SelectContent>
                              {subLedgersLoading ? (
                                <div className="flex items-center justify-center p-2">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  <span className="ml-2 text-sm">Loading...</span>
                                </div>
                              ) : subLedgersError ? (
                                <div className="p-2 text-sm text-red-500">
                                  Failed to load sub ledgers
                                </div>
                              ) : subLedgers.length === 0 ? (
                                <div className="p-2 text-sm text-muted-foreground">
                                  No sub ledgers available
                                </div>
                              ) : (
                                subLedgers.map((sl) => {
                                  const key = `${sl.type ?? 'NotSet'}-${sl.code}`;
                                  return (
                                    <SelectItem key={key} value={sl.code}>
                                      {sl.description ? `${sl.description} (${sl.code})` : sl.code}
                                    </SelectItem>
                                  );
                                })
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Barcode/QR Code Section */}
                    <BarcodeQRCodeSection
                      barcodeType={editedAsset.barcodeType}
                      barcodeValue={editedAsset.barcodeValue}
                      onCodeChange={(barcodeType, barcodeValue) => {
                        handleChange('barcodeType', barcodeType);
                        handleChange('barcodeValue', barcodeValue);
                      }}
                      disabled={isViewMode}
                      locationCode={locations.find(l => l.name === editedAsset.location)?.code}
                      assetNumericId={editedAsset.id}
                    />
                  </div>
                )}

                {/* Values Tab */}
                {activeTab === 'values' && (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Asset Values ({yearValues.length > 0 ? yearValues[0].year : new Date().getFullYear()})</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {yearValuesLoading ? (
                          <div className="flex items-center justify-center p-8">
                            <Loader2 className="h-6 w-6 animate-spin mr-2" />
                            <span>Loading asset values...</span>
                          </div>
                        ) : yearValuesError ? (
                          <div className="p-4 text-center text-red-500">
                            Failed to load asset values. Please try again.
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-5">
                              {/* Cost Section */}
                              <div className="md:col-span-5">
                                <h4 className="font-medium mb-2">Cost</h4>
                                <div className="grid grid-cols-5 gap-2">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Start of Year</Label>
                                    <Input value={formatCurrency((yearValues[0] || emptyYearValues).cost.startOfYear)} disabled className="bg-muted" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Additions</Label>
                                    <Input value={formatCurrency((yearValues[0] || emptyYearValues).cost.additions)} disabled className="bg-muted" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Disposals</Label>
                                    <Input value={formatCurrency((yearValues[0] || emptyYearValues).cost.disposals)} disabled className="bg-muted" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Revaluation</Label>
                                    <Input value={formatCurrency((yearValues[0] || emptyYearValues).cost.revaluation)} disabled className="bg-muted" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs font-bold">End of Year</Label>
                                    <Input value={formatCurrency((yearValues[0] || emptyYearValues).cost.endOfYear)} disabled className="bg-muted font-bold" />
                                  </div>
                                </div>
                              </div>

                              {/* Depreciation Section */}
                              <div className="md:col-span-5">
                                <h4 className="font-medium mb-2">Depreciation</h4>
                                <div className="grid grid-cols-5 gap-2">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Start of Year</Label>
                                    <Input value={formatCurrency((yearValues[0] || emptyYearValues).depreciation.startOfYear)} disabled className="bg-muted" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Charge</Label>
                                    <Input value={formatCurrency((yearValues[0] || emptyYearValues).depreciation.charge)} disabled className="bg-muted" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Disposals</Label>
                                    <Input value={formatCurrency((yearValues[0] || emptyYearValues).depreciation.disposals)} disabled className="bg-muted" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Revaluation</Label>
                                    <Input value={formatCurrency((yearValues[0] || emptyYearValues).depreciation.revaluation)} disabled className="bg-muted" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs font-bold">End of Year</Label>
                                    <Input value={formatCurrency((yearValues[0] || emptyYearValues).depreciation.endOfYear)} disabled className="bg-muted font-bold" />
                                  </div>
                                </div>
                              </div>

                              {/* Net Book Value Section */}
                              <div className="md:col-span-5">
                                <h4 className="font-medium mb-2">Net Book Value</h4>
                                <div className="grid grid-cols-2 gap-2 max-w-md">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Start of Year</Label>
                                    <Input value={formatCurrency((yearValues[0] || emptyYearValues).netBookValue.startOfYear)} disabled className="bg-muted" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">End of Year</Label>
                                    <Input value={formatCurrency((yearValues[0] || emptyYearValues).netBookValue.endOfYear)} disabled className="bg-muted" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Depreciation Settings</CardTitle>
                      </CardHeader>
                      <CardContent className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="depreciationMethod">Depreciation Method</Label>
                          <Select
                            value={editedAsset.depreciationMethod ?? 'Straight Line'}
                            onValueChange={(value: string) => handleChange('depreciationMethod', value)}
                            disabled={isViewMode}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Straight Line">Straight Line</SelectItem>
                              <SelectItem value="Reducing Balance">Reducing Balance</SelectItem>
                              <SelectItem value="Sum of Digits">Sum of Digits</SelectItem>
                              <SelectItem value="Period Ratio">Period Ratio</SelectItem>
                              <SelectItem value="None">None</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Hide Percentage when method is Sum of Digits */}
                        {editedAsset.depreciationMethod !== 'Sum of Digits' && (
                          <div className="space-y-2">
                            <Label htmlFor="depreciationPercentage">Percentage</Label>
                            <Input
                              id="depreciationPercentage"
                              type="number"
                              step="0.01"
                              value={editedAsset.depreciationPercentage ?? ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('depreciationPercentage', parseFloat(e.target.value) ?? undefined)}
                              disabled={isViewMode}
                              readOnly={isViewMode}
                              className={isViewMode ? 'bg-muted cursor-not-allowed' : ''}
                            />
                          </div>
                        )}
                        
                        {/* Hide Years when method is Reducing Balance */}
                        {editedAsset.depreciationMethod !== 'Reducing Balance' && (
                          <div className="space-y-2">
                            <Label htmlFor="depreciationYears">Years</Label>
                            <Input
                              id="depreciationYears"
                              type="number"
                              value={(editedAsset.depreciationYears || editedAsset.usefulLife) ?? ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('depreciationYears', parseInt(e.target.value) ?? undefined)}
                              disabled={isViewMode}
                              readOnly={isViewMode}
                              className={isViewMode ? 'bg-muted cursor-not-allowed' : ''}
                            />
                          </div>
                        )}
                        
                        {/* Hide Frequency when method is Sum of Digits */}
                        {editedAsset.depreciationMethod !== 'Sum of Digits' && (
                          <div className="space-y-2">
                            <Label htmlFor="postingFrequency">Posting Frequency</Label>
                            <Select
                              value={editedAsset.postingFrequency ?? 'Monthly'}
                              onValueChange={(value: string) => handleChange('postingFrequency', value)}
                              disabled={isViewMode}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Monthly">Monthly</SelectItem>
                                <SelectItem value="Quarterly">Quarterly</SelectItem>
                                <SelectItem value="Half Yearly">Half Yearly</SelectItem>
                                <SelectItem value="Yearly">Yearly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {/* Show In Service Period/Year only for Straight Line or None */}
                        {(editedAsset.depreciationMethod === 'Straight Line' || editedAsset.depreciationMethod === 'None') && (
                          <>
                            <div className="space-y-2">
                              <Label htmlFor="inServicePeriod">In Service Period</Label>
                              <Input
                                id="inServicePeriod"
                                type="number"
                                value={editedAsset.inServicePeriod ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('inServicePeriod', parseInt(e.target.value) ?? undefined)}
                                disabled={isViewMode || isEditMode}
                                readOnly={isViewMode || isEditMode}
                                className={(isViewMode || isEditMode) ? 'bg-muted cursor-not-allowed' : ''}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="inServiceYear">In Service Year</Label>
                              <Select
                                value={editedAsset.inServiceYear?.toString() ?? ''}
                                onValueChange={(value: string) => handleChange('inServiceYear', parseInt(value))}
                                disabled={isViewMode || isEditMode || systemYearsLoading}
                              >
                                <SelectTrigger id="inServiceYear" className={(isViewMode || isEditMode) ? 'bg-muted cursor-not-allowed' : ''}>
                                  <SelectValue placeholder={systemYearsLoading ? "Loading years..." : "Select year"} />
                                </SelectTrigger>
                                <SelectContent>
                                  {systemYearsLoading ? (
                                    <div className="flex items-center justify-center p-2">
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                      <span className="ml-2 text-sm">Loading...</span>
                                    </div>
                                  ) : systemYearsError ? (
                                    <div className="p-2 text-sm text-red-500">
                                      Failed to load years
                                    </div>
                                  ) : systemYears.length === 0 ? (
                                    <div className="p-2 text-sm text-muted-foreground">
                                      No years available
                                    </div>
                                  ) : (
                                    systemYears.map((year) => (
                                      <SelectItem key={year.id} value={year.nominal.toString()}>
                                        {year.label}
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          </>
                        )}
                        
                        <div className="space-y-2">
                          <Label htmlFor="nextPostingPeriod">Next Posting Period</Label>
                          <Input
                            id="nextPostingPeriod"
                            type="number"
                            value={editedAsset.nextPostingPeriod ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('nextPostingPeriod', parseInt(e.target.value) ?? undefined)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                            className={isViewMode ? 'bg-muted cursor-not-allowed' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="nextPostingYear">Next Posting Year</Label>
                          <Select
                            value={editedAsset.nextPostingYear?.toString() ?? ''}
                            onValueChange={(value: string) => handleChange('nextPostingYear', parseInt(value))}
                            disabled={isViewMode || systemYearsLoading}
                          >
                            <SelectTrigger id="nextPostingYear" className={isViewMode ? 'bg-muted cursor-not-allowed' : ''}>
                              <SelectValue placeholder={systemYearsLoading ? "Loading years..." : "Select year"} />
                            </SelectTrigger>
                            <SelectContent>
                              {systemYearsLoading ? (
                                <div className="flex items-center justify-center p-2">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  <span className="ml-2 text-sm">Loading...</span>
                                </div>
                              ) : systemYearsError ? (
                                <div className="p-2 text-sm text-red-500">
                                  Failed to load years
                                </div>
                              ) : systemYears.length === 0 ? (
                                <div className="p-2 text-sm text-muted-foreground">
                                  No years available
                                </div>
                              ) : (
                                systemYears.map((year) => (
                                  <SelectItem key={year.id} value={year.nominal.toString()}>
                                    {year.label}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Hide Postings Made/Total when method is Reducing Balance */}
                        {editedAsset.depreciationMethod !== 'Reducing Balance' && (
                          <>
                            <div className="space-y-2">
                              <Label htmlFor="postingsMade">Postings Made</Label>
                              <Input
                                id="postingsMade"
                                type="number"
                                value={editedAsset.postingsMade ?? 0}
                                disabled
                                className="bg-muted"
                              />
                              <p className="text-xs text-muted-foreground">Read-only</p>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="totalPostings">Total Postings</Label>
                              <Input
                                id="totalPostings"
                                type="number"
                                value={editedAsset.totalPostings ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('totalPostings', parseInt(e.target.value) ?? undefined)}
                                disabled={isViewMode}
                                readOnly={isViewMode}
                                className={isViewMode ? 'bg-muted cursor-not-allowed' : ''}
                              />
                            </div>
                          </>
                        )}
                        
                        <div className="space-y-2 flex items-center gap-2 md:col-span-2">
                          <Switch
                            id="doNotDepreciate"
                            checked={editedAsset.doNotDepreciate ?? false}
                            onCheckedChange={(checked) => handleChange('doNotDepreciate', checked)}
                            disabled={isViewMode}
                          />
                          <Label htmlFor="doNotDepreciate" className="cursor-pointer">
                            Do Not Depreciate
                          </Label>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Show Base Value card only for Straight Line, Period Ratio, or None */}
                    {(editedAsset.depreciationMethod === 'Straight Line' || 
                      editedAsset.depreciationMethod === 'Period Ratio' || 
                      editedAsset.depreciationMethod === 'None') && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Base Value</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="baseValueOn">Base Value On</Label>
                            <Select
                              value={editedAsset.baseValueOn ?? 'remaining'}
                              onValueChange={(value: string) => handleChange('baseValueOn', value)}
                              disabled={isViewMode}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="remaining">Remaining Postings</SelectItem>
                                <SelectItem value="original">Original Postings</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        <div className="space-y-2">
                          <Label htmlFor="remainingPostings">Remaining Postings</Label>
                          <Input
                            id="remainingPostings"
                            type="number"
                            value={editedAsset.remainingPostings ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('remainingPostings', parseInt(e.target.value) ?? undefined)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="originalPostings">Original Postings</Label>
                          <Input
                            id="originalPostings"
                            type="number"
                            value={editedAsset.originalPostings ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('originalPostings', parseInt(e.target.value) ?? undefined)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="currentBaseValue">Current Base Value</Label>
                          <Input
                            id="currentBaseValue"
                            type="number"
                            step="0.01"
                            value={editedAsset.currentBaseValue ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('currentBaseValue', parseFloat(e.target.value) ?? undefined)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                      </CardContent>
                    </Card>
                    )}

                    <Card>
                      <CardHeader>
                        <CardTitle>Nominals and Costing</CardTitle>
                        <CardDescription>Coming soon</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          This section will be available in a future update.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Supplier Tab */}
                {activeTab === 'supplier' && (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Supplier Information</CardTitle>
                      </CardHeader>
                      <CardContent className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="supplierName">Supplier (Picklist)</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                id="supplierName"
                                variant="outline"
                                role="combobox"
                                className="w-full justify-between font-normal"
                                disabled={isViewMode || suppliersLoading}
                              >
                                {suppliersLoading ? (
                                  <span className="flex items-center">
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Loading...
                                  </span>
                                ) : editedAsset.supplierId ? (
                                  (() => {
                                    const selectedSupplier = suppliers.find(s => s.code === editedAsset.supplierId);
                                    return selectedSupplier 
                                      ? `${selectedSupplier.code} - ${selectedSupplier.name}`
                                      : editedAsset.supplierName || editedAsset.supplierId;
                                  })()
                                ) : (
                                  "Select or enter supplier"
                                )}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-full p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search suppliers..." />
                                <CommandEmpty>No supplier found.</CommandEmpty>
                                <CommandGroup>
                                  {suppliers.map((supplier) => (
                                    <CommandItem
                                      key={supplier.primaryKey}
                                      value={`${supplier.code} ${supplier.name ?? ""}`}
                                      onSelect={() => {
                                        handleChange('supplierId', supplier.code);
                                        handleChange('supplierName', supplier.name ?? supplier.code);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          editedAsset.supplierId === supplier.code ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      <span className="font-medium">{supplier.code}</span>
                                      <span className="ml-2 text-muted-foreground">{supplier.name}</span>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          {suppliersError && (
                            <p className="text-sm text-destructive">Failed to load suppliers</p>
                          )}
                        </div>
                        <div className="space-y-2 flex items-center gap-2">
                          <Switch
                            id="hirePurchase"
                            checked={editedAsset.hirePurchase ?? false}
                            onCheckedChange={(checked) => handleChange('hirePurchase', checked)}
                            disabled={isViewMode}
                          />
                          <Label htmlFor="hirePurchase" className="cursor-pointer">
                            Hire Purchase
                          </Label>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="invoiceReference">Invoice Reference</Label>
                          <Input
                            id="invoiceReference"
                            value={editedAsset.invoiceReference ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('invoiceReference', e.target.value)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="purchaseOrderNumber">Purchase Order Reference</Label>
                          <Input
                            id="purchaseOrderNumber"
                            value={editedAsset.purchaseOrderNumber ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('purchaseOrderNumber', e.target.value)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="warrantyStartDate">Warranty Start Date</Label>
                          <Input
                            id="warrantyStartDate"
                            type="date"
                            value={formatDateForInput(editedAsset.warrantyStartDate)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('warrantyStartDate', e.target.value)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="warrantyExpiryDate">Warranty Finish Date</Label>
                          <Input
                            id="warrantyExpiryDate"
                            type="date"
                            value={formatDateForInput(editedAsset.warrantyExpiryDate)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('warrantyExpiryDate', e.target.value)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="warrantyCost">Warranty Cost</Label>
                          <Input
                            id="warrantyCost"
                            type="number"
                            step="0.01"
                            value={editedAsset.warrantyCost ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('warrantyCost', parseFloat(e.target.value) ?? undefined)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Supplier Addresses</CardTitle>
                        <CardDescription>
                          {editedAsset.supplierName 
                            ? `Addresses for ${editedAsset.supplierName} from Financials`
                            : 'Select a supplier to view their addresses'}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {(() => {
                            if (supplierAddressesLoading) {
                              return (
                                <div className="flex items-center justify-center py-4">
                                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                  <span className="ml-2 text-sm text-muted-foreground">Loading addresses...</span>
                                </div>
                              );
                            }
                            if (financialsSupplierAddresses.length > 0) {
                              return (
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Contact</TableHead>
                                      <TableHead>Address</TableHead>
                                      <TableHead>Town</TableHead>
                                      <TableHead>Post Code</TableHead>
                                      <TableHead>Country</TableHead>
                                      <TableHead>Type</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {financialsSupplierAddresses.map((address) => (
                                      <TableRow key={address.summarySection?.primaryKey ?? `address-${address.details?.postCode ?? ''}-${address.details?.town ?? ''}`}>
                                        <TableCell>{address.summarySection?.completeContactName ?? '-'}</TableCell>
                                        <TableCell>{address.details?.address ?? '-'}</TableCell>
                                        <TableCell>{address.details?.town ?? '-'}</TableCell>
                                        <TableCell>{address.details?.postCode ?? '-'}</TableCell>
                                        <TableCell>{address.details?.country ?? '-'}</TableCell>
                                        <TableCell>
                                          <div className="flex flex-wrap gap-1">
                                            {address.summarySection?.isOrder && (
                                              <Badge variant="outline" className="text-xs">Order</Badge>
                                            )}
                                            {address.summarySection?.isRemittance && (
                                              <Badge variant="outline" className="text-xs">Remittance</Badge>
                                            )}
                                            {address.summarySection?.isDelivery && (
                                              <Badge variant="outline" className="text-xs">Delivery</Badge>
                                            )}
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              );
                            }
                            if (editedAsset.supplierName) {
                              return <p className="text-sm text-muted-foreground">No addresses found for this supplier.</p>;
                            }
                            return <p className="text-sm text-muted-foreground">Select a supplier to view their addresses.</p>;
                          })()}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Equipment Tab */}
                {activeTab === 'equipment' && (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Linked Equipment</CardTitle>
                        <CardDescription>Equipment linked to this asset</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newEquipment: EquipmentLink = {
                                id: Date.now().toString(),
                                equipmentId: '',
                                equipmentName: '',
                                linkedDate: new Date().toISOString().split('T')[0],
                              };
                              setLinkedEquipment([...linkedEquipment, newEquipment]);
                            }}
                            disabled={isViewMode}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Link Equipment
                          </Button>

                          {linkedEquipment.length > 0 ? (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Equipment ID</TableHead>
                                  <TableHead>Equipment Name</TableHead>
                                  <TableHead>Description</TableHead>
                                  <TableHead>Linked Date</TableHead>
                                  <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {linkedEquipment.map((equipment) => (
                                  <TableRow key={equipment.id}>
                                    <TableCell>{equipment.equipmentId}</TableCell>
                                    <TableCell>{equipment.equipmentName}</TableCell>
                                    <TableCell>{equipment.description}</TableCell>
                                    <TableCell>{equipment.linkedDate}</TableCell>
                                    <TableCell>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setLinkedEquipment(linkedEquipment.filter((e) => e.id !== equipment.id));
                                        }}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <p className="text-sm text-muted-foreground">No equipment linked yet.</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Custom Fields Tab */}
                {activeTab === 'custom-fields' && (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Value Fields</CardTitle>
                        <CardDescription>Custom numeric value fields</CardDescription>
                      </CardHeader>
                      <CardContent className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="userValue1">{getLabel('userValue1')}</Label>
                          <Input
                            id="userValue1"
                            type="number"
                            step="0.01"
                            value={editedAsset.userValue1 ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('userValue1', parseFloat(e.target.value) ?? undefined)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="userValue2">{getLabel('userValue2')}</Label>
                          <Input
                            id="userValue2"
                            type="number"
                            step="0.01"
                            value={editedAsset.userValue2 ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('userValue2', parseFloat(e.target.value) ?? undefined)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="userValue3">{getLabel('userValue3')}</Label>
                          <Input
                            id="userValue3"
                            type="number"
                            step="0.01"
                            value={editedAsset.userValue3 ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('userValue3', parseFloat(e.target.value) ?? undefined)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="userValue4">{getLabel('userValue4')}</Label>
                          <Input
                            id="userValue4"
                            type="number"
                            step="0.01"
                            value={editedAsset.userValue4 ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('userValue4', parseFloat(e.target.value) ?? undefined)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="userValue5">{getLabel('userValue5')}</Label>
                          <Input
                            id="userValue5"
                            type="number"
                            step="0.01"
                            value={editedAsset.userValue5 ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('userValue5', parseFloat(e.target.value) ?? undefined)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Key Fields</CardTitle>
                        <CardDescription>Custom text/reference fields</CardDescription>
                      </CardHeader>
                      <CardContent className="grid gap-4 md:grid-cols-2">
                        <UserKeyCombobox
                          id="userKey1"
                          label={getLabel('userKey1')}
                          value={editedAsset.userKey1 ?? ''}
                          options={userKey1Options}
                          onChange={(value) => handleChange('userKey1', value)}
                          disabled={isViewMode}
                          loading={userKeysLoading}
                        />
                        <UserKeyCombobox
                          id="userKey2"
                          label={getLabel('userKey2')}
                          value={editedAsset.userKey2 ?? ''}
                          options={userKey2Options}
                          onChange={(value) => handleChange('userKey2', value)}
                          disabled={isViewMode}
                          loading={userKeysLoading}
                        />
                        <UserKeyCombobox
                          id="userKey3"
                          label={getLabel('userKey3')}
                          value={editedAsset.userKey3 ?? ''}
                          options={userKey3Options}
                          onChange={(value) => handleChange('userKey3', value)}
                          disabled={isViewMode}
                          loading={userKeysLoading}
                        />
                        <UserKeyCombobox
                          id="userKey4"
                          label={getLabel('userKey4')}
                          value={editedAsset.userKey4 ?? ''}
                          options={userKey4Options}
                          onChange={(value) => handleChange('userKey4', value)}
                          disabled={isViewMode}
                          loading={userKeysLoading}
                        />
                        <UserKeyCombobox
                          id="userKey5"
                          label={getLabel('userKey5')}
                          value={editedAsset.userKey5 ?? ''}
                          options={userKey5Options}
                          onChange={(value) => handleChange('userKey5', value)}
                          disabled={isViewMode}
                          loading={userKeysLoading}
                        />
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Date Fields</CardTitle>
                        <CardDescription>Custom date fields</CardDescription>
                      </CardHeader>
                      <CardContent className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="userDate1">{getLabel('userDate1')}</Label>
                          <Input
                            id="userDate1"
                            type="date"
                            value={formatDateForInput(editedAsset.userDate1)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('userDate1', e.target.value)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="userDate2">{getLabel('userDate2')}</Label>
                          <Input
                            id="userDate2"
                            type="date"
                            value={formatDateForInput(editedAsset.userDate2)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('userDate2', e.target.value)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="userDate3">{getLabel('userDate3')}</Label>
                          <Input
                            id="userDate3"
                            type="date"
                            value={formatDateForInput(editedAsset.userDate3)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('userDate3', e.target.value)}
                            disabled={isViewMode}
                            readOnly={isViewMode}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Notes Tab */}
                {activeTab === 'notes' && (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Notes</CardTitle>
                        <CardDescription>Additional information about this asset</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          id="notes"
                          value={editedAsset.notes ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleChange('notes', e.target.value)}
                          rows={10}
                          placeholder="Enter any additional notes or information about this asset..."
                          disabled={isViewMode}
                          readOnly={isViewMode}
                        />
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Documents Tab */}
                {activeTab === 'documents' && !isCreateMode && (
                  <DocumentsTab
                    assetId={editedAsset.id ?? ''}
                    disabled={isViewMode}
                    onDocumentsChange={() => {
                      // Refresh documents if needed
                    }}
                  />
                )}

                {/* Alerts Tab */}
                {activeTab === 'alerts' && !isCreateMode && (
                  <AssetAlertsTab
                    assetId={editedAsset.id ?? ''}
                    assetName={editedAsset.name}
                    asset={editedAsset}
                    disabled={isViewMode}
                    onAlertsChange={(count) => {
                      // Could update a badge or notification count here
                      console.log(`Asset has ${count} active alerts`);
                    }}
                  />
                )}

                {/* Photos Tab */}
                {activeTab === 'photos' && (
                  <AssetPhotosTab
                    photos={editedAsset.photos ?? []}
                    onChange={(photos) => handleChange('photos', photos)}
                    disabled={isViewMode}
                    maxPhotos={5}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
  );

  // Render with or without Dialog wrapper based on mode
  if (mode === 'page') {
    return (
      <>
        {content}
        <AssetConfirmChangesModal
          open={showConfirmModal}
          onOpenChange={setShowConfirmModal}
          originalAsset={originalAsset}
          editedAsset={editedAsset}
          onConfirm={handleConfirmSave}
        />
        <AssetUnsavedChangesModal
          open={showUnsavedChangesModal}
          onOpenChange={setShowUnsavedChangesModal}
          onConfirmLeave={handleConfirmLeave}
        />
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-6xl w-[95vw] sm:w-[90vw] lg:w-[85vw] xl:w-[1200px] h-[85vh] p-0 gap-0 overflow-hidden">
          {content}
        </DialogContent>
      </Dialog>
      <AssetConfirmChangesModal
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        originalAsset={originalAsset}
        editedAsset={editedAsset}
        onConfirm={handleConfirmSave}
      />
      <AssetUnsavedChangesModal
        open={showUnsavedChangesModal}
        onOpenChange={setShowUnsavedChangesModal}
        onConfirmLeave={handleConfirmLeave}
      />
    </>
  );
}
